import { Box, Grid } from '@mui/material'
import { Input } from 'components/core-ui/Input'
import { Modal } from 'components/core-ui/Modal'
import { useFormik } from 'formik'
import { ChangeEvent, useMemo } from 'react'
import { getInitialValues, getValidationSchema } from './helpers'
import s from 'components/core-ui/Modal/Modal.modules.scss'
import sl from '../../../../partners.modules.scss'
import { Button, Checkbox } from '@fullscreendigital/carrefour-dex-fe'
import { makeCorePatchRequest } from 'utils/api'
import { useMutation } from 'react-query'
import { useNotification } from 'hooks'
import { handleQueryError } from 'utils/helpers'
import { toPersonPayload } from './serializers'

const EditIdentityDetailsModal = props => {
  const { isModalOpen, onModalClose, person, refetchAssignment } = props

  const { showNotification } = useNotification()

  const initialValues = useMemo(() => getInitialValues(person), [person])

  const validationSchema = useMemo(() => getValidationSchema(), [])

  const mutatePerson = async values => {
    await makeCorePatchRequest(`/iam/external-partners/people/${person.id}`, values)
  }

  const { isLoading, mutate } = useMutation(mutatePerson, {
    onSuccess: res => {
      refetchAssignment && refetchAssignment()
      closeModal()
      formik.resetForm()
      showNotification({
        message: 'Identity details updated successfully.',
        type: 'success'
      })
    },
    onError: error =>
      handleQueryError(error, 'Failed to update identity details.', showNotification)
  })

  const formik = useFormik({
    enableReinitialize: true,
    validateOnChange: false,
    initialValues: initialValues,
    validationSchema: validationSchema,
    onSubmit: async values => {
      mutate(toPersonPayload(values))
    }
  })

  const closeModal = () => {
    onModalClose()
    formik.resetForm()
  }

  return (
    <Modal
      isModalOpen={isModalOpen}
      closeModal={closeModal}
      modalTitle="Edit Identity Details"
      modalWidth="50%"
    >
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Input
            type="text"
            variant="outlined"
            name="firstName"
            label="First Name"
            width="100%"
            value={formik.values.firstName}
            onChange={formik.handleChange}
            required
            disabled
            error={!!formik.errors.firstName}
            helperText={formik.errors.firstName as string}
          />
        </Grid>
        <Grid item xs={12}>
          <Input
            type="text"
            variant="outlined"
            name="lastName"
            label="Last Name"
            width="100%"
            value={formik.values.lastName}
            onChange={formik.handleChange}
            required
            disabled
            error={!!formik.errors.lastName}
            helperText={formik.errors.lastName as string}
          />
        </Grid>
        <Grid item xs={9}>
          <Input
            type="text"
            variant="outlined"
            name="email"
            label="External email"
            width="100%"
            value={formik.values.email}
            onChange={formik.handleChange}
            disabled={formik.values.emailNotProvided}
            error={!!formik.errors.email}
            helperText={formik.errors.email as string}
          />
        </Grid>
        <Grid item xs={3} style={{ display: 'flex', alignItems: 'center', lineHeight: '56px' }}>
          <Box className={sl.checkboxWrapper} style={!!formik.errors.email ? { top: '-6px' } : {}}>
            <Checkbox
              checked={formik.values.emailNotProvided}
              label="Not provided"
              onChange={() => {
                if (!formik.values.emailNotProvided) {
                  formik.setFieldValue('email', '')
                }
                formik.setFieldValue('emailNotProvided', !formik.values.emailNotProvided)
              }}
            />
          </Box>
        </Grid>
        <Grid item xs={9}>
          <Input
            type="text"
            variant="outlined"
            name="phone"
            label="Phone"
            width="100%"
            value={formik.values.phone}
            onChange={formik.handleChange}
            disabled={formik.values.phoneNotProvided}
            error={!!formik.errors.phone}
            helperText={formik.errors.phone as string}
          />
        </Grid>
        <Grid item xs={3} style={{ display: 'flex', alignItems: 'center', lineHeight: '56px' }}>
          <Box className={sl.checkboxWrapper} style={!!formik.errors.phone ? { top: '-6px' } : {}}>
            <Checkbox
              checked={formik.values.phoneNotProvided}
              label="Not provided"
              onChange={() => {
                if (!formik.values.phoneNotProvided) {
                  formik.setFieldValue('phone', '')
                }
                formik.setFieldValue('phoneNotProvided', !formik.values.phoneNotProvided)
              }}
            />
          </Box>
        </Grid>
        <Grid item xs={12}>
          <Input
            type="textarea"
            name="details"
            label="Partner details"
            style={{ width: '100%' }}
            rows={3}
            variant="outlined"
            value={formik.values.details}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              formik.setFieldValue('details', event.currentTarget.value)
            }
            error={!!formik.errors.details}
            helperText={formik.errors.details as string}
          />
        </Grid>
        <Grid item xs={12} className={s.modalButtons}>
          <Button variant="outlined" label="Cancel" onClick={closeModal} />
          <Button
            variant="contained"
            label="Save"
            onClick={() => formik.handleSubmit()}
            disabled={isLoading}
          />
        </Grid>
      </Grid>
    </Modal>
  )
}

export default EditIdentityDetailsModal
